import { useState, useEffect, useRef, useCallback } from 'react';
import { cloud } from '../utils/cloudStorage';
import { haptic } from '../utils/haptic';

interface UseDraftOptions<T> {
  key: string;
  initialValue: T;
  saveInterval?: number;
}

interface UseDraftReturn<T> {
  data: T;
  setData: (updater: T | ((prev: T) => T)) => void;
  hasDraft: boolean;
  savedDraft: T | null;
  restoreDraft: () => void;
  discardDraft: () => void;
  clearDraft: () => void;
  isLoading: boolean;
}

export function useDraft<T>({
  key,
  initialValue,
  saveInterval = 5000,
}: UseDraftOptions<T>): UseDraftReturn<T> {
  const [data, setDataState] = useState<T>(initialValue);
  const [savedDraft, setSavedDraft] = useState<T | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const dataRef = useRef(data);
  const dirtyRef = useRef(false);

  dataRef.current = data;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const draft = await cloud.get<T>(key);
      if (cancelled) return;

      if (draft) {
        setSavedDraft(draft);
        setHasDraft(true);
      }
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (dirtyRef.current) {
        await cloud.set(key, dataRef.current);
        dirtyRef.current = false;
      }
    }, saveInterval);

    return () => clearInterval(timer);
  }, [key, saveInterval]);

  useEffect(() => {
    return () => {
      if (dirtyRef.current) {
        cloud.set(key, dataRef.current);
      }
    };
  }, [key]);

  const setData = useCallback((updater: T | ((prev: T) => T)) => {
    setDataState((prev) => {
      const next =
        typeof updater === 'function' ? (updater as (prev: T) => T)(prev) : updater;
      dataRef.current = next;
      dirtyRef.current = true;
      return next;
    });
  }, []);

  const restoreDraft = useCallback(() => {
    if (savedDraft) {
      setDataState(savedDraft);
      dataRef.current = savedDraft;
      setHasDraft(false);
      setSavedDraft(null);
      haptic.success();
    }
  }, [savedDraft]);

  const discardDraft = useCallback(() => {
    cloud.remove(key);
    setSavedDraft(null);
    setHasDraft(false);
    haptic.tap();
  }, [key]);

  const clearDraft = useCallback(() => {
    cloud.remove(key);
    dirtyRef.current = false;
  }, [key]);

  return {
    data,
    setData,
    hasDraft,
    savedDraft,
    restoreDraft,
    discardDraft,
    clearDraft,
    isLoading,
  };
}
